import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Upload, ChartLine as LineChart, Settings, Target, ChevronLeft, ChevronRight, ChevronDown, Activity, Zap, ShieldCheck, Cpu, Layers, Files, PencilLine, ShieldAlert, Database, Monitor, FileText, Brain } from 'lucide-react';
import PriceRunner from './PriceRunner';
import CuratorControls from '../curator/CuratorControls';
import { TABS } from '../../data/types';

// "Bloomberg Terminal" — expandable nav group directly under Dashboard.
// Sub-items reuse the exact sector list/order the in-page Terminal tabs already use.
export const TERMINAL_GROUP = {
  to: '/terminal',
  icon: Monitor,
  label: '"Bloomberg Terminal"',
  sectors: TABS.map((t) => ({ to: `/terminal/${t.id}`, label: t.label, id: t.id })),
};

// Nav labels (B3/B7/B22): honest product names — no multi-source ingestion theater.
export const NAV_ITEMS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/analysis', icon: LineChart, label: 'Analysis' },
  { to: '/bulk-review', icon: Files, label: 'Bulk Review' },
  { to: '/indicators', icon: Activity, label: 'Indicators' },
  { to: '/fractal', icon: Layers, label: 'Fractals' },
  { to: '/intelligence', icon: Zap, label: 'Intelligence' },
  { to: '/auto-trader', icon: Cpu, label: 'AutoTrader' },
  { to: '/tradingview', icon: Activity, label: 'TradingView' },
  { to: '/annotate', icon: PencilLine, label: 'Annotate' },
  { to: '/audit', icon: ShieldAlert, label: 'Audit' },
  { to: '/report', icon: FileText, label: 'Report' },
  { to: '/learning', icon: Brain, label: 'Learning' },
  { to: '/ingestion', icon: Database, label: 'Data Pipeline' },
  { to: '/scorer', icon: Target, label: 'Scorer' },
  { to: '/health', icon: ShieldCheck, label: 'Health' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

/** Page-level honesty blurbs for topbar (B7/B22). Empty string = no extra line. */
export const PAGE_HONESTY_COPY = {
  '/annotate': 'Grading requires a live backend + active analysis. Failures show as errors — not silent success.',
  '/report': 'Live analysis only. Empty until you select an analysis (demo: ?demo=1).',
  '/ingestion': 'Read-only pipeline status from the engine — not multi-vendor key vault theater.',
};

export default function DashboardLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [terminalOpen, setTerminalOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const isOnTerminal = location.pathname.startsWith('/terminal');
  const honestyKey = Object.keys(PAGE_HONESTY_COPY).find(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
  );
  const honestyCopy = honestyKey ? PAGE_HONESTY_COPY[honestyKey] : null;

  const renderNavItem = ({ to, icon: Icon, label, isHighlighted }) => {
    const isActive = location.pathname === to ||
      (to !== '/' && location.pathname.startsWith(to));
    
    return (
      <NavLink
        key={to}
        to={to}
        data-testid={`nav-${label.toLowerCase()}`}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors duration-200 group ${
          isHighlighted && !isActive
            ? 'bg-[#f5b400]/5 border border-[#f5b400]/30 text-[#f5b400] hover:bg-[#f5b400]/10'
            : isActive
              ? 'bg-[#f5b400]/10 text-[#f5b400] border-l-2 border-[#f5b400]'
              : 'text-[#a1a1aa] hover:text-white hover:bg-white/5'
        } ${collapsed ? 'justify-center' : ''}`}
      >
        <Icon className={`w-4 h-4 flex-shrink-0 ${
          isHighlighted || isActive ? 'text-[#f5b400]' : 'group-hover:text-white'
        }`} />
        {!collapsed && (
          <span className="truncate">
            {label}
            {isHighlighted && !isActive && (
              <span className="ml-1.5 text-[9px] bg-[#f5b400] text-black px-1 py-0.5 rounded font-bold">
                LIVE
              </span>
            )}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <div className="flex flex-col h-screen bg-[#0e0f12] overflow-hidden">
      {/* Global Price Runner - appears on all pages */}
      <PriceRunner />
      
      <div className="flex flex-1 overflow-hidden">
        {/* Grain overlay */}
        <div className="grain-overlay pointer-events-none" />

        {/* Sidebar */}
        <aside className={`${collapsed ? 'wk-sidebar-collapsed' : 'wk-sidebar'} flex flex-col py-4 transition-all duration-300 relative z-20`}>
          {/* Logo */}
          <div className={`flex items-center gap-3 px-4 mb-4 ${collapsed ? 'justify-center' : ''}`}>
            <div className="w-8 h-8 rounded-sm bg-[#f5b400] flex items-center justify-center flex-shrink-0">
              <Activity className="w-5 h-5 text-black" />
            </div>
            {!collapsed && (
              <div className="animate-fade-in">
                <h1 className="text-sm font-bold tracking-tight text-white">WaveKit</h1>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#a1a1aa]">Wave Analyzer</p>
              </div>
            )}
          </div>

          {/* "Bloomberg Terminal" — expandable yellow nav group, directly under Dashboard */}
          <div className="px-2 mb-3">
            <button
              type="button"
              onClick={() => {
                setTerminalOpen((v) => !v);
                if (!isOnTerminal) navigate(TERMINAL_GROUP.to);
              }}
              data-testid="nav-bloomberg-terminal-toggle"
              aria-expanded={terminalOpen}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors duration-200 group ${
                isOnTerminal
                  ? 'bg-[#f5b400]/15 border border-[#f5b400]/40 text-[#f5b400]'
                  : 'bg-[#f5b400]/5 border border-[#f5b400]/30 text-[#f5b400] hover:bg-[#f5b400]/10'
              } ${collapsed ? 'justify-center' : ''}`}
            >
              <Monitor className="w-4 h-4 flex-shrink-0 text-[#f5b400]" />
              {!collapsed && (
                <>
                  <span className="flex-1 text-left truncate font-semibold">
                    {TERMINAL_GROUP.label}
                    <span className="ml-1.5 text-[9px] bg-[#f5b400] text-black px-1 py-0.5 rounded font-bold">
                      LIVE
                    </span>
                  </span>
                  <ChevronDown
                    className={`w-3.5 h-3.5 flex-shrink-0 text-[#f5b400] transition-transform duration-200 ${terminalOpen ? 'rotate-180' : ''}`}
                  />
                </>
              )}
            </button>

            {/* Sector sub-items — drives the Terminal page's existing tab-selection state via route param */}
            {!collapsed && terminalOpen && (
              <div className="mt-1 ml-3 pl-3 border-l border-[#f5b400]/20 space-y-0.5 animate-fade-in">
                {TERMINAL_GROUP.sectors.map((sector) => {
                  const isActiveSector = location.pathname === sector.to ||
                    (sector.id === 'warroom' && location.pathname === '/terminal');
                  return (
                    <NavLink
                      key={sector.to}
                      to={sector.to}
                      data-testid={`nav-terminal-${sector.id}`}
                      className={`block px-2.5 py-1.5 rounded-sm text-[11px] tracking-wide transition-colors duration-150 ${
                        isActiveSector
                          ? 'bg-[#f5b400]/10 text-[#f5b400] font-semibold'
                          : 'text-[#a1a1aa] hover:text-[#f5b400] hover:bg-[#f5b400]/5'
                      }`}
                    >
                      {sector.label}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>

          <div className="px-2 mb-2">
            <div className="h-px bg-zinc-800" />
          </div>

          {/* Nav */}
          <nav className="flex-1 space-y-1 px-2 overflow-y-auto" data-testid="sidebar-nav">
            {NAV_ITEMS.map(renderNavItem)}
          </nav>

          {/* Curator Mode — unobtrusive footer control; no password in bundle */}
          <CuratorControls collapsed={collapsed} />

          {/* Collapse toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            data-testid="sidebar-toggle"
            className="mx-2 mt-4 p-2 rounded-sm hover:bg-white/5 text-[#a1a1aa] hover:text-white transition-colors"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto relative">
          {/* Topbar */}
          <div className="wk-topbar sticky top-0 z-10 flex items-center justify-between px-6">
            <div className="flex min-w-0 flex-col gap-0.5">
              <h2 className="text-sm font-medium text-[#a1a1aa]" data-testid="topbar-page-title">
                {isOnTerminal
                  ? (TERMINAL_GROUP.sectors.find(s => s.to === location.pathname)?.label || 'BLOOMBERG TERMINAL — WAR ROOM')
                  : (NAV_ITEMS.find(n => n.to === location.pathname || (n.to !== '/' && location.pathname.startsWith(n.to)))?.label || 'WaveKit')}
              </h2>
              {honestyCopy && (
                <p className="truncate text-[10px] font-mono text-[#8b93a7]/80" data-testid="topbar-honesty">
                  {honestyCopy}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-wider font-mono text-[#a1a1aa]/50">v0.1.0</span>
            </div>
          </div>

          {/* Page content */}
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
